/**
 * Auth schemas — mirror `auth.users`, `auth.workspaces`,
 * `auth.workspace_memberships` per ARCHITECTURE_v5 §4.2.
 *
 * These schemas are reused on three sides of the boundary:
 *   - frontend (apps/chaos-ui) — form validation + typed API client
 *   - gateway (apps/chaos-gateway) — request body validation
 *   - auth service (apps/chaos-auth-service) — drizzle-kit codegen reference
 *
 * Update `auth/migrations/*` together with this file (Phase 1).
 */
import { z } from 'zod';

// Roles available within a workspace. Mirrors auth.workspace_memberships.role
// CHECK constraint. Adding a new role requires a sibling-bumped migration
// and a coordinated update of permissions middleware in chaos-gateway.
export const workspaceRoles = [
  'founder',
  'admin',
  'employee',
  'external',
  'auditor',
  'tuner',
] as const;
export const workspaceRoleSchema = z.enum(workspaceRoles);
export type WorkspaceRole = z.infer<typeof workspaceRoleSchema>;

// auth.users (display surface)
export const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().min(1).max(120).nullable(),
  isActive: z.boolean(),
  emailVerified: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  lastLoginAt: z.string().datetime().nullable(),
  /**
   * A1.6 (SPEC-00 §6, W2-9). `auth.users.system_role` — the identity's standing
   * over the INSTALLATION, not a seat in any Workspace. `'founder'` is the
   * instance operator (`org-auth.ts::requireInstanceOperator`).
   *
   * WHY IT IS ON THE WIRE. `publicUser()` has emitted it since `32c9091`; until
   * this line the shared schema did not declare it, so the UI could not read
   * what the gateway was already sending and derived founder-ness instead from
   * `GET /orgs/:id/members/me`'s `viaSystemFounder`. That probe answers a
   * DIFFERENT question — with `ACCOUNT_SCOPED_SUPERADMIN` on it is narrowed to
   * orgs the founder's own account owns — so the instance operator reads FALSE
   * while looking at a Workspace attached to somebody else's account, and the
   * catalog-CRUD affordances (which are `requireSystemFounder` and org-
   * independent) vanish for the one person they are for. It also cannot answer
   * at all before an active org resolves.
   *
   * OPTIONAL, deliberately: a payload minted before this field existed must
   * still parse, and a UI that treats `undefined` as "not the operator" fails
   * closed. Not an enum here — the closed vocabulary belongs to the gateway,
   * and a wire schema that rejects a role added server-side would log everybody
   * out on a deploy.
   */
  systemRole: z.string().optional(),
  // Account avatar (profile photo) — a STABLE same-origin serve URL
  // (/api/auth/me/avatar?v=...) when set, else null. Optional so older
  // payloads without the field still satisfy the type. Decoupled from any
  // org logo — this is the per-USER photo only.
  avatarUrl: z.string().nullable().optional(),
});
export type User = z.infer<typeof userSchema>;

// auth.workspaces
export const workspaceSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits, single hyphen separators'),
  createdAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
});
export type Workspace = z.infer<typeof workspaceSchema>;

// auth.workspace_memberships
export const workspaceMembershipSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  userId: z.string().uuid(),
  role: workspaceRoleSchema,
  joinedAt: z.string().datetime(),
  leftAt: z.string().datetime().nullable(),
});
export type WorkspaceMembership = z.infer<typeof workspaceMembershipSchema>;

// auth.sessions — exposed to the client only via the refresh-rotation
// endpoint; the JWT body itself never contains the refresh token hash.
export const sessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
});
export type Session = z.infer<typeof sessionSchema>;

// === Password policy (ADR-053) ============================================
// Minimum 14 chars, must include upper + lower + digit + symbol. HIBP check
// runs server-side in chaos-auth-service (k-anonymity: only first 5 chars of
// the SHA-1 hash leave the network).
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{14,}$/;
export const passwordSchema = z
  .string()
  .min(14, 'Password must be at least 14 characters long')
  .max(256, 'Password must be at most 256 characters long')
  .regex(PASSWORD_REGEX, 'Password must contain upper, lower, digit and symbol characters');

// === Request payloads =====================================================
export const signupRequestSchema = z.object({
  email: z.string().email(),
  password: passwordSchema,
  displayName: z.string().min(1).max(120),
});
export type SignupRequest = z.infer<typeof signupRequestSchema>;

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(256),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

// Password-reset schemas removed in Phase 1.5 (ADR-053 v1.1).
// Internal tool: founder manages passwords out-of-band via direct DB access.

// === Response payloads ====================================================
export const authSessionResponseSchema = z.object({
  user: userSchema,
  workspace: workspaceSchema,
  membership: workspaceMembershipSchema,
  accessToken: z.string().min(20),
  // Refresh tokens live in httpOnly cookies; this field is for non-browser
  // clients (CLI / Telegram bridge) and may be omitted in the cookie flow.
  refreshToken: z.string().min(20).optional(),
});
export type AuthSessionResponse = z.infer<typeof authSessionResponseSchema>;

export const meResponseSchema = z.object({
  user: userSchema,
  // Present for cookie-authenticated browser sessions. Bearer clients are
  // intentionally exempt from the unsafe-request principal fence.
  principalFence: z.string().regex(/^[A-Za-z0-9_-]{43}$/).optional(),
  workspaces: z.array(
    z.object({
      workspace: workspaceSchema,
      membership: workspaceMembershipSchema,
    }),
  ),
});
export type MeResponse = z.infer<typeof meResponseSchema>;
